import { useRef } from 'react';

import { useHouseholdRouteType } from '@/utils/hooks/useHouseholdRoutePaths';
import { useGlobalAccount } from '@/views/household/household-queries';
import {
    HouseholdNoteForm,
    HouseholdNoteFormRef,
    HouseholdNoteFormType,
} from '@/views/household/HouseholdNotes/HouseholdNoteForm';
import { useAddNoteMutation } from '@/views/household/HouseholdNotes/note-queries';
import { Toast } from '@/widgets/Toast';
import { getHouseholdName } from 'utils';

import { useDockState } from '../dock-state';
import { CreateNoteDockItem as CreateNoteDockItemType } from '../dock-state-types';

export const CreateNoteDockItem = (props: { item: CreateNoteDockItemType }) => {
    const { data: globalAccount } = useGlobalAccount(props.item.data.globalAccountGUID);
    const { setActiveId, updateDockItem, removeDockItem } = useDockState();
    const formRef = useRef<HouseholdNoteFormRef>(null);
    const addNoteMutation = useAddNoteMutation(props.item.data?.globalAccountGUID ?? '');
    const { householdTypeLabel } = useHouseholdRouteType();

    return (
        <div
            onBlur={() => {
                const formValues = formRef.current?.formMethods.getValues();
                if (formValues) {
                    updateDockItem({
                        ...props.item,
                        data: {
                            ...props.item.data,
                            subject: formValues?.subject || '',
                            content: formValues?.content || '',
                            householdOption: formValues?.householdOption,
                        },
                    });
                }
            }}
        >
            <HouseholdNoteForm
                key={props.item.id}
                ref={formRef}
                globalAccount={globalAccount}
                onSubmit={(data: HouseholdNoteFormType) => {
                    if (addNoteMutation.isPending) return;
                    const householdGUID = props.item.data.householdOption?.value ?? props.item.data.globalAccountGUID;
                    if (!householdGUID) {
                        Toast.forError(
                            new Error(`No ${householdTypeLabel} selected`),
                            `Please select a ${householdTypeLabel}`,
                        );
                        return;
                    }
                    addNoteMutation
                        .mutateAsync({
                            subject: data.subject,
                            content: data.content,
                            householdGUID,
                            eventGUID: props.item.data.eventGUID,
                        })
                        .then(() => {
                            Toast.success('Note created successfully');
                            removeDockItem(props.item.id);
                        })
                        .catch((error) => {
                            Toast.forError(error, 'Failed to create note');
                        });
                }}
                defaultValues={{
                    subject: props.item.data.subject,
                    content: props.item.data.content,
                    householdOption: props.item.data.householdOption
                        ? props.item.data.householdOption
                        : {
                              value: props.item.data.globalAccountGUID ?? '',
                              label: globalAccount ? getHouseholdName(globalAccount) : `Unnamed ${householdTypeLabel}`,
                          },
                }}
                onCancel={() => setActiveId(null)}
                showHouseholdInput={props.item.data.showHouseholdInput}
            />
        </div>
    );
};
